/**
 * Normalizes configuration data based on the specified app type (llu or libre1)
 *
 * @param rawConfig - The raw configuration object to be normalized
 * @param country - The country code for which the configuration is being normalized
 * @param app - App type ('llu' or 'libre1') that determines the normalization strategy
 * @returns A normalized Config object with standardized structure
 * @throws Will throw an error with status 400 if normalization fails or app is not supported
 */
export const normalizeConfig = (rawConfig, country, app) => {
    try {
        switch (app) {
            case 'llu':
                return normalizeLluConfig(rawConfig);
            case 'libre1':
                return normalizeLibreConfig(rawConfig, country);
            default:
                throw { status: 400, message: `App not supported: ${app}` };
        }
    }
    catch (error) {
        throw { status: 400, message: `Error normalizing config: ${error || 'Unknown error'}` };
    }
};
const normalizeLluConfig = (rawConfig) => {
    const lluConfig = rawConfig.base;
    const config = {
        minAge: lluConfig.minCaregiverAge,
        apiCountryUrls: lluConfig.lluAPICountryURLs,
        i18n: lluConfig.i18n ?? { allowList: [], fallBacks: { default: ['en-US'] } },
        supportedLanguages: lluConfig.supportedLanguages,
        supportedCountries: Object.entries(lluConfig.supportedCountries).map(([key]) => key),
        countries: [],
        passwordRules: lluConfig.passwordRules,
        documentUrls: {
            files: lluConfig.documentsUrl,
        },
        documentPaths: {
            files: {
                termsOfUse: lluConfig.documents.toullu,
                privacyNotice: lluConfig.documents.pnllu,
            },
        },
        validation: {
            patient: {
                firstNameMaxLength: lluConfig.validationRules?.firstName?.stringMaxLength ?? 40,
                lastNameMaxLength: lluConfig.validationRules?.lastName?.stringMaxLength ?? 80,
            },
        },
    };
    return config;
};
const normalizeLibreConfig = (rawConfig, country) => {
    const passwordRules = rawConfig.validation.identity.map(({ data, type, message }) => ({
        data,
        type,
        message,
    }));
    const config = {
        minAge: rawConfig.adultAges[country],
        apiCountryUrls: rawConfig.libreApiCountryUrls,
        i18n: rawConfig.i18n ?? { allowList: [], fallBacks: { default: ['en-US'] } },
        supportedLanguages: rawConfig.languages,
        supportedCountries: rawConfig.supportedCountries,
        countries: rawConfig.countries,
        passwordRules,
        documentUrls: rawConfig.documentUrls,
        documentPaths: {
            cms: { ...rawConfig.documentPaths.cms },
            files: {
                termsOfUse: rawConfig.documentPaths.files.toulibre,
                privacyNotice: rawConfig.documentPaths.files.pnlibre,
            },
        },
        validation: {
            patient: {
                firstNameMaxLength: rawConfig.validation?.libreApi?.patient?.firstName?.stringMaxLength ?? 40,
                lastNameMaxLength: rawConfig.validation?.libreApi?.patient?.lastName?.stringMaxLength ?? 80,
            },
        },
    };
    return config;
};
